import { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useDashboardData } from '../hooks/useDashboardData';
import { api } from '../utils/api';
import { SiteMap } from '../components/dashboard/SiteMap';
import { SearchableSelect } from '../components/layout/SearchableSelect';
import { UserRole } from '../types/index';
import './Organizations.css';

type Tab = 'home' | 'users' | 'config';

export function Organizations() {
  const { 
    organizations, 
    activeOrganization, 
    changeActiveOrganization, 
    sites, 
    units, 
    isSuperAdmin, 
    currentUserId, 
    refetch 
  } = useDashboardData();
  
  const [activeTab, setActiveTab] = useState<Tab>('home');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState(UserRole.Viewer);
  const [inviteMessage, setInviteMessage] = useState('');
  const [users, setUsers] = useState<any[]>([]);

  // Configuration Form State
  const [orgConfig, setOrgConfig] = useState({ name: '' });

  // Create Organization State
  const [showCreateOrgModal, setShowCreateOrgModal] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');

  // Assign Site State
  const [showAssignSiteModal, setShowAssignSiteModal] = useState(false);
  const [unassignedSites, setUnassignedSites] = useState<any[]>([]);
  const [selectedUnassignedSites, setSelectedUnassignedSites] = useState<string[]>([]);
  const [siteSearchQuery, setSiteSearchQuery] = useState('');

  // Spreadsheet filter states
  const [filterFirstName, setFilterFirstName] = useState('');
  const [filterLastName, setFilterLastName] = useState('');
  const [filterEmail, setFilterEmail] = useState('');
  const [filterRole, setFilterRole] = useState('');

  // Site spreadsheet filter & sort states
  const [filterSiteName, setFilterSiteName] = useState('');
  const [sortSiteName, setSortSiteName] = useState<'asc' | 'desc' | null>(null);

  // Spreadsheet sort states
  const [sortField, setSortField] = useState<'firstName' | 'lastName' | 'email' | 'role' | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(null);

  // Custom modal action states
  const [confirmAction, setConfirmAction] = useState<{
    type: 'revoke' | 'promote' | 'demote';
    userId: string;
    email: string;
  } | null>(null);
  const [confirmUnassignSite, setConfirmUnassignSite] = useState<any | null>(null);
  
  // Delete Organization State
  const [showDeleteOrgConfirm, setShowDeleteOrgConfirm] = useState(false);
  const [deletingOrg, setDeletingOrg] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');

  // Local filtered sites & units for the active organization
  const orgSites = useMemo(() => {
    if (!activeOrganization) return [];
    return sites.filter((s: any) => s.tenantId === activeOrganization.tenantId);
  }, [sites, activeOrganization]);

  const orgUnits = useMemo(() => {
    if (!activeOrganization) return [];
    const orgSiteIds = new Set(orgSites.map((s: any) => s.id));
    return units.filter((u: any) => orgSiteIds.has(u.siteId));
  }, [units, orgSites, activeOrganization]);

  const filteredUnassignedSites = useMemo(() => {
    return unassignedSites.filter(s => {
      const sid = s.PK?.replace('SITE#', '') || s.id || '';
      const name = s.name || '';
      return (
        name.toLowerCase().includes(siteSearchQuery.toLowerCase()) ||
        sid.toLowerCase().includes(siteSearchQuery.toLowerCase())
      );
    });
  }, [unassignedSites, siteSearchQuery]);

  const filteredAndSortedOrgSites = useMemo(() => {
    let result = [...orgSites];
    
    // Filter
    if (filterSiteName) {
      result = result.filter(site => 
        (site.name || '').toLowerCase().includes(filterSiteName.toLowerCase())
      );
    }
    
    // Sort
    if (sortSiteName) {
      result.sort((a, b) => {
        const nameA = a.name || '';
        const nameB = b.name || '';
        const comparison = nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: 'base' });
        return sortSiteName === 'asc' ? comparison : -comparison;
      });
    }
    
    return result;
  }, [orgSites, filterSiteName, sortSiteName]);

  const location = useLocation();
  const navigate = useNavigate();

  // Handle sidebar redirection selection
  useEffect(() => {
    if (location.state?.fromSidebar) {
      if (organizations && organizations.length > 0) {
        changeActiveOrganization(organizations[0].tenantId);
      }
      // Clear location state to avoid repeating on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, organizations, changeActiveOrganization, navigate]);

  useEffect(() => {
    if (activeOrganization) {
      setOrgConfig({ 
        name: activeOrganization.name || ''
      });
      setActiveTab('home');
    }
  }, [activeOrganization]);

  useEffect(() => {
    if (activeTab === 'users' && activeOrganization) {
      fetchUsers();
    }
  }, [activeTab, activeOrganization]);

  const fetchUsers = async () => {
    try {
      const tenantUsers = await api.get('/api/v1/tenants/me/users');
      setUsers(tenantUsers);
    } catch (err) {
      console.error(err);
    }
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const result = await api.post('/api/v1/invitations', {
        email: inviteEmail,
        role: inviteRole,
        tenantId: activeOrganization?.tenantId
      });
      setInviteMessage(`Invitation sent! Link: ${result.link}`);
      setInviteEmail('');
      fetchUsers();
    } catch (err: any) {
      setInviteMessage(`Error: ${err.message}`);
    }
  };

  const handleConfirmAction = async () => {
    if (!confirmAction) return;
    setActionLoading(true);
    setActionError('');
    try {
      if (confirmAction.type === 'revoke') {
        await api.delete(`/api/v1/tenants/me/users/${confirmAction.userId}`);
      } else if (confirmAction.type === 'promote') {
        await api.put(`/api/v1/tenants/me/users/${confirmAction.userId}/role`, {
          role: UserRole.OrgAdmin
        });
      } else if (confirmAction.type === 'demote') {
        await api.put(`/api/v1/tenants/me/users/${confirmAction.userId}/role`, {
          role: UserRole.Viewer
        });
      }
      setConfirmAction(null);
      fetchUsers();
    } catch (err: any) {
      setActionError(err.message || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteOrganization = async () => {
    if (!activeOrganization) return;
    setDeletingOrg(true);
    setActionError('');
    try {
      await api.delete('/api/v1/tenants/me');
      
      const remainingOrgs = organizations.filter(o => o.tenantId !== activeOrganization.tenantId);
      if (remainingOrgs.length > 0) {
        localStorage.setItem('activeTenantId', remainingOrgs[0].tenantId);
        changeActiveOrganization(remainingOrgs[0].tenantId);
      } else {
        localStorage.removeItem('activeTenantId');
      }

      setShowDeleteOrgConfirm(false);
      if (refetch) refetch();
    } catch (err: any) {
      setActionError(err.message || 'Failed to delete organization');
    } finally {
      setDeletingOrg(false);
    }
  };

  const handleSaveOrgConfig = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.put('/api/v1/tenants/me', {
        name: orgConfig.name
      });
      alert('Organization updated successfully');
      if (refetch) refetch();
    } catch (err: any) {
      alert(`Failed to save organization: ${err.message}`);
    }
  };

  const handleCreateOrg = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await api.post('/api/v1/tenants', { name: newOrgName });
      alert('Organization created successfully!');
      setShowCreateOrgModal(false);
      setNewOrgName('');
      if (refetch) refetch();
    } catch (err: any) {
      alert(`Failed to create organization: ${err.message}`);
    }
  };

  const openAssignSiteModal = async () => {
    try {
      const result = await api.get('/api/v1/sites/unassigned');
      // Filter out sites that are already assigned to the active organization
      const currentOrgSiteIds = new Set(orgSites.map((s: any) => s.id));
      const eligible = (result || []).filter((s: any) => {
        const sid = s.PK?.replace('SITE#', '') || s.id;
        return !currentOrgSiteIds.has(sid);
      });
      setUnassignedSites(eligible);
      setSelectedUnassignedSites([]);
      setSiteSearchQuery('');
      setShowAssignSiteModal(true);
    } catch (err: any) {
      alert(`Failed to fetch unassigned sites: ${err.message}`);
    }
  };

  const handleAssignSites = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeOrganization) return;
    try {
      await Promise.all(selectedUnassignedSites.map(siteId => {
        return api.post(`/api/v1/sites/${siteId}/assign`, {
          tenantId: activeOrganization.tenantId
        });
      }));
      setShowAssignSiteModal(false);
      if (refetch) refetch();
    } catch (err: any) {
      alert(`Failed to assign sites: ${err.message}`);
    }
  };

  const handleUnassignSite = async () => {
    if (!confirmUnassignSite || !activeOrganization) return;
    setActionLoading(true);
    setActionError('');
    try {
      await api.post(`/api/v1/sites/${confirmUnassignSite.id}/unassign`, {
        tenantId: activeOrganization.tenantId
      });
      setConfirmUnassignSite(null);
      if (refetch) refetch();
    } catch (err: any) {
      setActionError(err.message || 'Failed to remove site from organization');
    } finally {
      setActionLoading(false);
    }
  };

  // Sorting handler
  const toggleSort = (field: 'firstName' | 'lastName' | 'email' | 'role') => {
    if (sortField === field) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else {
        setSortField(null);
        setSortDirection(null);
      }
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Filtered and sorted users selector
  const filteredAndSortedUsers = useMemo(() => {
    return [...users]
      .filter(u => {
        const firstName = u.firstName || '';
        const lastName = u.lastName || '';
        const email = u.email || '';
        const roleDisplay = u.role === 'OrgAdmin' ? 'Org Admin' : u.role === 'Viewer' ? 'Viewer' : u.role || '';

        return (
          firstName.toLowerCase().includes(filterFirstName.toLowerCase()) &&
          lastName.toLowerCase().includes(filterLastName.toLowerCase()) &&
          email.toLowerCase().includes(filterEmail.toLowerCase()) &&
          roleDisplay.toLowerCase().includes(filterRole.toLowerCase())
        );
      })
      .sort((a, b) => {
        if (!sortField || !sortDirection) return 0;

        let valA = '';
        let valB = '';

        if (sortField === 'role') {
          valA = a.role === 'OrgAdmin' ? 'Org Admin' : a.role === 'Viewer' ? 'Viewer' : a.role || '';
          valB = b.role === 'OrgAdmin' ? 'Org Admin' : b.role === 'Viewer' ? 'Viewer' : b.role || '';
        } else {
          valA = a[sortField] || '';
          valB = b[sortField] || '';
        }

        const comparison = valA.localeCompare(valB, undefined, { numeric: true, sensitivity: 'base' });
        return sortDirection === 'asc' ? comparison : -comparison;
      });
  }, [users, filterFirstName, filterLastName, filterEmail, filterRole, sortField, sortDirection]);

  return (
    <div className="page organizations-page">
      <div className="organizations-header-row">
        <div className="organizations-header-left">
          <h1 className="page__title">Organizations</h1>
          <SearchableSelect
            id="org-select"
            value={activeOrganization?.tenantId || ''}
            onChange={(val) => {
              changeActiveOrganization(val);
            }}
            options={organizations.map(org => ({
              value: org.tenantId,
              label: org.name
            }))}
            placeholder={organizations.length > 0 ? "Select Organization..." : "You are not assigned to any organizations"}
            disabled={organizations.length === 0}
          />
        </div>
        {isSuperAdmin && (
          <button className="btn-create-org" onClick={() => setShowCreateOrgModal(true)}>
            Create Organization
          </button>
        )}
      </div>

      {activeOrganization && (
        <>
          <div className="tab-bar">
            <button className={`tab-btn ${activeTab === 'home' ? 'tab-btn--active' : ''}`} onClick={() => setActiveTab('home')}>Home</button>
            {(isSuperAdmin || (activeOrganization.role !== 'Viewer' && activeOrganization.role !== 'ReadOnly')) && (
              <>
                <button className={`tab-btn ${activeTab === 'users' ? 'tab-btn--active' : ''}`} onClick={() => setActiveTab('users')}>Users</button>
                <button className={`tab-btn ${activeTab === 'config' ? 'tab-btn--active' : ''}`} onClick={() => setActiveTab('config')}>Configuration</button>
              </>
            )}
          </div>

          <div className="tab-content-panel">
            {activeTab === 'home' && (
              <>
                <div className="card spreadsheet-card" style={{ marginBottom: '1.5rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--color-border-primary)' }}>
                    <h3 className="card-title" style={{ margin: 0 }}>Organization Sites</h3>
                    {isSuperAdmin && (
                      <button className="btn-add-site" onClick={openAssignSiteModal}>
                        Add Site
                      </button>
                    )}
                  </div>
                  <div className="spreadsheet-container">
                    <table className="spreadsheet-table">
                      <thead>
                        <tr>
                          <th 
                            onClick={() => {
                              if (sortSiteName === 'asc') {
                                setSortSiteName('desc');
                              } else if (sortSiteName === 'desc') {
                                setSortSiteName(null);
                              } else {
                                setSortSiteName('asc');
                              }
                            }} 
                            className="sortable-header"
                          >
                            Site Name <span className="sort-arrow">{sortSiteName === 'asc' ? '▲' : sortSiteName === 'desc' ? '▼' : '↕'}</span>
                          </th>
                          {isSuperAdmin && <th style={{ width: '150px' }}>Actions</th>}
                        </tr>
                        <tr className="filter-row">
                          <td>
                            <input
                              type="text"
                              placeholder="Filter Site Name..."
                              value={filterSiteName}
                              onChange={e => setFilterSiteName(e.target.value)}
                              onClick={e => e.stopPropagation()}
                            />
                          </td>
                          {isSuperAdmin && <td></td>}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredAndSortedOrgSites.length > 0 ? (
                          filteredAndSortedOrgSites.map(site => (
                            <tr key={site.id}>
                              <td>
                                <Link 
                                  to={`/dashboard/sites?siteId=${site.id}`} 
                                  className="site-title-link hyperlink"
                                  style={{ fontWeight: '600', color: 'var(--color-brand-primary)', textDecoration: 'none' }}
                                >
                                  {site.name}
                                </Link>
                              </td>
                              {isSuperAdmin && (
                                <td>
                                  <div className="action-buttons-cell">
                                    <button
                                      type="button"
                                      className="btn-revoke-action"
                                      onClick={() => setConfirmUnassignSite(site)}
                                    >
                                      Remove
                                    </button>
                                  </div>
                                </td>
                              )}
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={isSuperAdmin ? 2 : 1} style={{ textAlign: 'center', color: 'var(--color-text-secondary)', padding: '2rem' }}>
                              No sites found matching current filters.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="card">
                  <SiteMap units={orgUnits as any} sites={orgSites as any} activeSiteId={undefined} />
                </div>
              </>
            )}

            {activeTab === 'users' && (
              <div className="users-tab-layout two-col-layout">
                <div className="spreadsheet-column" style={{ flex: 2.3 }}>
                  <div className="card spreadsheet-card">
                    <div className="spreadsheet-container">
                      <table className="spreadsheet-table">
                        <thead>
                          <tr>
                            <th onClick={() => toggleSort('firstName')} className="sortable-header">
                              First Name <span className="sort-arrow">{sortField === 'firstName' ? (sortDirection === 'asc' ? '▲' : '▼') : '↕'}</span>
                            </th>
                            <th onClick={() => toggleSort('lastName')} className="sortable-header">
                              Last Name <span className="sort-arrow">{sortField === 'lastName' ? (sortDirection === 'asc' ? '▲' : '▼') : '↕'}</span>
                            </th>
                            <th onClick={() => toggleSort('email')} className="sortable-header">
                              Email <span className="sort-arrow">{sortField === 'email' ? (sortDirection === 'asc' ? '▲' : '▼') : '↕'}</span>
                            </th>
                            <th onClick={() => toggleSort('role')} className="sortable-header">
                              Access Level <span className="sort-arrow">{sortField === 'role' ? (sortDirection === 'asc' ? '▲' : '▼') : '↕'}</span>
                            </th>
                            <th>Actions</th>
                          </tr>
                          <tr className="filter-row">
                            <td>
                              <input
                                type="text"
                                placeholder="Filter First Name..."
                                value={filterFirstName}
                                onChange={e => setFilterFirstName(e.target.value)}
                                onClick={e => e.stopPropagation()}
                              />
                            </td>
                            <td>
                              <input
                                type="text"
                                placeholder="Filter Last Name..."
                                value={filterLastName}
                                onChange={e => setFilterLastName(e.target.value)}
                                onClick={e => e.stopPropagation()}
                              />
                            </td>
                            <td>
                              <input
                                type="text"
                                placeholder="Filter Email..."
                                value={filterEmail}
                                onChange={e => setFilterEmail(e.target.value)}
                                onClick={e => e.stopPropagation()}
                              />
                            </td>
                            <td>
                              <input
                                type="text"
                                placeholder="Filter Level..."
                                value={filterRole}
                                onChange={e => setFilterRole(e.target.value)}
                                onClick={e => e.stopPropagation()}
                              />
                            </td>
                            <td></td>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredAndSortedUsers.length > 0 ? (
                            filteredAndSortedUsers.map(u => {
                              const isSelf = u.userId === currentUserId;
                              const isTargetOrgAdmin = u.role === 'OrgAdmin';
                              const isTargetSuperAdmin = u.role === 'SuperAdmin';
                              
                              // OrgAdmin cannot revoke or promote other OrgAdmins or SuperAdmins
                              const isCallerOrgAdmin = activeOrganization.role === 'OrgAdmin';
                              const canManage = isSuperAdmin || (isCallerOrgAdmin && !isTargetOrgAdmin && !isTargetSuperAdmin);
                              
                              const isViewer = u.role === UserRole.Viewer;

                              return (
                                <tr key={u.userId}>
                                  <td>{u.firstName || '—'}</td>
                                  <td>{u.lastName || '—'}</td>
                                  <td className="email-cell">{u.email}</td>
                                  <td>{u.role === 'OrgAdmin' ? 'Org Admin' : u.role === 'Viewer' ? 'Viewer' : u.role}</td>
                                  <td>
                                    <div className="action-buttons-cell">
                                      <button
                                        type="button"
                                        className="btn-promote-action"
                                        disabled={!canManage || isSelf || !isViewer}
                                        onClick={() => setConfirmAction({ type: 'promote', userId: u.userId, email: u.email })}
                                        title={!isViewer ? 'Only Viewers can be promoted' : 'Promote user to Org Admin'}
                                      >
                                        Promote
                                      </button>
                                      {isSuperAdmin && isTargetOrgAdmin && (
                                        <button
                                          type="button"
                                          className="btn-demote-action"
                                          disabled={isSelf}
                                          onClick={() => setConfirmAction({ type: 'demote', userId: u.userId, email: u.email })}
                                          title="Demote user to Viewer"
                                        >
                                          Demote
                                        </button>
                                      )}
                                      <button
                                        type="button"
                                        className="btn-revoke-action"
                                        disabled={!canManage || isSelf || isTargetSuperAdmin}
                                        onClick={() => setConfirmAction({ type: 'revoke', userId: u.userId, email: u.email })}
                                        title="Revoke access"
                                      >
                                        Revoke
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })
                          ) : (
                            <tr>
                              <td colSpan={6} style={{ textAlign: 'center', color: 'var(--color-text-secondary)', padding: '2rem' }}>
                                No users found matching current filters.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                <div className="col-divider"></div>

                <div className="invite-column" style={{ flex: 1, minWidth: '300px' }}>
                  <div className="card invite-card">
                    <form onSubmit={handleInvite}>
                      <div className="invite-header-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.25rem' }}>
                        <div style={{ flex: 1, paddingRight: '1rem' }}>
                          <h3 className="card-title" style={{ margin: 0 }}>Invite</h3>
                          <p className="invite-desc" style={{ margin: '0.25rem 0 0 0', fontSize: '0.85rem', color: 'var(--color-text-secondary)' }}>
                            Users invited here will have full access to <strong>{activeOrganization.name}</strong>.
                          </p>
                        </div>
                        <button type="submit" className="btn-send-invite" style={{ flexShrink: 0, marginTop: '4px' }}>Send Invite</button>
                      </div>
                      
                      <div className="invite-inputs-row" style={{ display: 'flex', gap: '0.75rem' }}>
                        <input 
                          type="email" 
                          placeholder="Email Address" 
                          value={inviteEmail} 
                          onChange={e => setInviteEmail(e.target.value)} 
                          required 
                          className="invite-email-input"
                          style={{ flex: 2 }}
                        />
                        <select 
                          value={inviteRole} 
                          onChange={e => setInviteRole(e.target.value as any)} 
                          required
                          className="invite-role-select"
                          style={{ flex: 1 }}
                        >
                          {isSuperAdmin && <option value={UserRole.OrgAdmin}>Org Admin</option>}
                          <option value={UserRole.Viewer}>Viewer</option>
                        </select>
                      </div>
                    </form>
                    {inviteMessage && (
                      <p style={{ 
                        marginTop: '1rem', 
                        color: inviteMessage.startsWith('Error') ? 'var(--color-status-critical)' : 'var(--color-brand-primary)',
                        fontSize: '0.875rem',
                        fontWeight: 500
                      }}>
                        {inviteMessage}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'config' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                <div className="card">
                  <div className="config-form" style={{ maxWidth: '500px' }}>
                    <form onSubmit={handleSaveOrgConfig}>
                      <div className="form-group">
                        <label style={{ fontWeight: 'bold' }}>Organization Name</label>
                        <input 
                          type="text" 
                          value={orgConfig.name} 
                          onChange={e => setOrgConfig({...orgConfig, name: e.target.value})}
                          required
                          className="org-name-input"
                        />
                      </div>
                      <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
                        <button type="submit" className="btn-save-changes">Save Changes</button>
                      </div>
                    </form>
                  </div>
                </div>

                {isSuperAdmin && (
                  <div className="card card-danger-zone">
                    <h3 style={{ color: 'var(--color-status-critical)', marginTop: 0, marginBottom: '0.5rem', fontSize: '1.1rem', fontWeight: 600 }}>Danger Zone</h3>
                    <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginBottom: '1.25rem' }}>
                      Permanently delete this organization, including all its members and configurations. This action cannot be undone.
                    </p>
                    <button 
                      type="button" 
                      className="btn-danger" 
                      onClick={() => {
                        setActionError('');
                        setShowDeleteOrgConfirm(true);
                      }}
                    >
                      Delete Organization
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* Confirmation Modal */}
      {confirmAction && (
        <div className="modal-overlay">
          <div className="modal-content confirmation-modal">
            <h2>{confirmAction.type === 'revoke' ? 'Revoke User Access' : confirmAction.type === 'promote' ? 'Promote User to Org Admin' : 'Demote User to Viewer'}</h2>
            <div className="modal-body" style={{ marginBottom: '1.5rem' }}>
              {confirmAction.type === 'revoke' ? (
                <p>
                  Are you sure you want to revoke access for <strong>{confirmAction.email}</strong>? They will lose all access to this organization.
                </p>
              ) : confirmAction.type === 'promote' ? (
                <p>
                  Are you sure you want to promote <strong>{confirmAction.email}</strong> to Org Admin? They will have full administrative privileges in this organization.
                </p>
              ) : (
                <p>
                  Are you sure you want to demote <strong>{confirmAction.email}</strong> to Viewer? They will lose administrative privileges and have view-only access in this organization.
                </p>
              )}
              {actionError && (
                <p className="error-message" style={{ color: 'var(--color-status-critical)', marginTop: '1rem', fontWeight: 500 }}>
                  {actionError}
                </p>
              )}
            </div>
            <div className="modal-actions">
              <button 
                type="button" 
                className="btn-secondary" 
                onClick={() => setConfirmAction(null)}
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className={confirmAction.type === 'revoke' ? 'btn-danger' : confirmAction.type === 'demote' ? 'btn-danger btn-demote-action' : 'btn-confirm-action'}
                onClick={handleConfirmAction}
                disabled={actionLoading}
              >
                {actionLoading ? 'Processing...' : confirmAction.type === 'revoke' ? 'Revoke Access' : confirmAction.type === 'promote' ? 'Promote User' : 'Demote User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Organization Confirmation Modal */}
      {showDeleteOrgConfirm && (
        <div className="modal-overlay">
          <div className="modal-content confirmation-modal">
            <h2>Delete Organization</h2>
            <div className="modal-body" style={{ marginBottom: '1.5rem' }}>
              <p>
                Are you sure you want to permanently delete the organization <strong>{activeOrganization?.name}</strong>?
              </p>
              <p style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: 'var(--color-status-critical)', fontWeight: 500 }}>
                This action is irreversible and will delete all user assignments and site access configurations for this organization.
              </p>
              {actionError && (
                <p className="error-message" style={{ color: 'var(--color-status-critical)', marginTop: '1rem', fontWeight: 500 }}>
                  {actionError}
                </p>
              )}
            </div>
            <div className="modal-actions">
              <button 
                type="button" 
                className="btn-secondary" 
                onClick={() => {
                  setShowDeleteOrgConfirm(false);
                  setActionError('');
                }}
                disabled={deletingOrg}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="btn-danger"
                onClick={handleDeleteOrganization}
                disabled={deletingOrg}
              >
                {deletingOrg ? 'Deleting...' : 'Delete Organization'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unassign Site Confirmation Modal */}
      {confirmUnassignSite && (
        <div className="modal-overlay">
          <div className="modal-content confirmation-modal">
            <h2>Remove Site from Organization</h2>
            <div className="modal-body" style={{ marginBottom: '1.5rem' }}>
              <p>
                Are you sure you want to remove the site <strong>{confirmUnassignSite.name}</strong> from <strong>{activeOrganization?.name}</strong>?
              </p>
              <p style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: 'var(--color-status-critical)' }}>
                This site will become unassigned and won't belong to any organization.
              </p>
              {actionError && (
                <p className="error-message" style={{ color: 'var(--color-status-critical)', marginTop: '1rem', fontWeight: 500 }}>
                  {actionError}
                </p>
              )}
            </div>
            <div className="modal-actions">
              <button 
                type="button" 
                className="btn-secondary" 
                onClick={() => {
                  setConfirmUnassignSite(null);
                  setActionError('');
                }}
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="btn-danger"
                onClick={handleUnassignSite}
                disabled={actionLoading}
              >
                {actionLoading ? 'Removing...' : 'Remove Site'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Org Modal */}
      {showCreateOrgModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Create New Organization</h2>
            <form onSubmit={handleCreateOrg} className="config-form">
              <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                <label style={{ fontWeight: 'bold' }}>Organization Name</label>
                <input 
                  type="text" 
                  value={newOrgName} 
                  onChange={e => setNewOrgName(e.target.value)} 
                  placeholder="e.g. Acme Corp"
                  required 
                  className="org-name-input"
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowCreateOrgModal(false)}>Cancel</button>
                <button type="submit" className="btn-save-changes" disabled={!newOrgName}>
                  Create Organization
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Assign Site Modal */}
      {showAssignSiteModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Assign Unassigned Sites</h2>
            <form onSubmit={handleAssignSites} className="config-form">
              <p style={{ marginBottom: '1rem', color: 'var(--text-secondary)' }}>
                Select the sites you want to assign to <strong>{activeOrganization?.name}</strong>.
              </p>
              <div className="form-group" style={{ marginBottom: '1rem' }}>
                <input 
                  type="text"
                  placeholder="Search unassigned sites by name or ID..."
                  value={siteSearchQuery}
                  onChange={(e) => setSiteSearchQuery(e.target.value)}
                  className="org-name-input"
                  style={{ width: '100%' }}
                />
              </div>
              <div 
                className="assign-sites-list"
                style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: '0.75rem', 
                  marginBottom: '1.5rem', 
                  maxHeight: '33vh', 
                  overflowY: 'auto',
                  border: '1px solid var(--color-border-primary)',
                  borderRadius: 'var(--radius-sm)',
                  padding: '0.75rem',
                  backgroundColor: 'var(--color-bg-secondary)'
                }}
              >
                {filteredUnassignedSites.length === 0 ? (
                  <p style={{ color: 'var(--text-secondary)', margin: 0 }}>
                    {unassignedSites.length === 0 ? 'No unassigned sites available.' : 'No matching sites found.'}
                  </p>
                ) : (
                  filteredUnassignedSites.map(s => {
                    const sid = s.PK?.replace('SITE#', '') || s.id;
                    return (
                      <label key={sid} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', color: 'var(--text-primary)', fontSize: '0.95rem' }}>
                        <input 
                          type="checkbox" 
                          checked={selectedUnassignedSites.includes(sid)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedUnassignedSites([...selectedUnassignedSites, sid]);
                            } else {
                              setSelectedUnassignedSites(selectedUnassignedSites.filter(id => id !== sid));
                            }
                          }}
                        />
                        {s.name} <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>({sid})</span>
                      </label>
                    );
                  })
                )}
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowAssignSiteModal(false)}>Cancel</button>
                <button type="submit" className="btn-save-changes" disabled={selectedUnassignedSites.length === 0}>
                  Assign Selected Sites
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Organizations;
